import { Injectable } from '@angular/core';

/** Resolves the browser client's public IPv4/IPv6 once per session (best-effort). */
@Injectable({ providedIn: 'root' })
export class ClientIpService {
  private resolvePromise: Promise<string | null> | null = null;

  /** Cached public IP for attaching to user message persistence requests. */
  resolve(): Promise<string | null> {
    if (!this.resolvePromise) {
      this.resolvePromise = this.fetchPublicIp();
    }
    return this.resolvePromise;
  }

  private async fetchPublicIp(): Promise<string | null> {
    if (typeof fetch !== 'function') {
      return null;
    }
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer =
      controller != null
        ? setTimeout(() => controller.abort(), 4000)
        : null;
    try {
      const res = await fetch('https://api.ipify.org?format=json', {
        method: 'GET',
        signal: controller?.signal,
      });
      if (!res.ok) {
        return null;
      }
      const data = (await res.json()) as { ip?: unknown };
      const ip = typeof data?.ip === 'string' ? data.ip.trim() : '';
      if (ip.length === 0 || ip.length > 100 || this.isUnusableClientIp(ip)) {
        return null;
      }
      return ip;
    } catch {
      return null;
    } finally {
      if (timer != null) {
        clearTimeout(timer);
      }
    }
  }

  private isUnusableClientIp(ip: string): boolean {
    const n = ip.toLowerCase().startsWith('::ffff:') ? ip.slice(7).trim().toLowerCase() : ip.toLowerCase();
    if (n === '127.0.0.1' || n.startsWith('127.') || n === '::1' || n === 'localhost') {
      return true;
    }
    if (n.startsWith('10.') || n.startsWith('192.168.') || n.startsWith('169.254.')) {
      return true;
    }
    if (n.startsWith('172.')) {
      const second = Number(n.split('.')[1]);
      return Number.isFinite(second) && second >= 16 && second <= 31;
    }
    return false;
  }
}
